import { useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { KnowledgeKind, KnowledgeSource, KnowledgeSourceList, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { usePlatform } from '../app/platform';
import { type AsyncState, toProblem, useAsync } from '../app/useAsync';
import { DeleteDialog } from './DeleteDialog';
import { KnowledgeSourceDialog } from './KnowledgeSourceDialog';

export interface KnowledgeSourcesPanelProps {
  workspaceId: string;
  /** 知識ソースの一覧。読み込みと再取得は呼び出し側（KnowledgePage）が持つ。 */
  state: AsyncState<KnowledgeSourceList>;
}

/**
 * 知識ソースの一覧と、外部ソースの接続（追加・編集・有効 / 無効・接続確認・削除。S6 E16）。
 * 種類ごとの記述子（kinds）は provider の登録から来るので、記述子の無い行は「使えない種類」として編集させない。
 */
export function KnowledgeSourcesPanel({ workspaceId, state }: KnowledgeSourcesPanelProps) {
  const { api } = usePlatform();
  const kinds = useAsync(() => api.listKnowledgeKinds(workspaceId), [api, workspaceId]);
  const [editing, setEditing] = useState<{ kind: KnowledgeKind; source: KnowledgeSource | null } | null>(null);
  const [deleting, setDeleting] = useState<KnowledgeSource | null>(null);
  const [notice, setNotice] = useState<{ tone: 'status' | 'alert'; text: string } | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [selected, setSelected] = useState('');
  // 実行中の行の id（二度押しで同じ expectedRevision の PATCH が 2 本飛ぶと、自分の 1 回目が原因で 409 になる）。
  const [running, setRunning] = useState<string | null>(null);

  const available = kinds.data?.kinds ?? [];
  const descriptors = new Map(available.map((kind) => [kind.kind, kind]));
  // kinds が未着・失敗のあいだは descriptors が空なので、記述子が無いことを「使えない種類」と読み替えない。
  // （useAsync は失敗時に data を null にするため、区別しないと /kinds が 1 度落ちただけで全行が使えない扱いで固定される）
  const kindsKnown = kinds.data !== null;
  const sources = state.data?.items ?? [];
  const connected = new Set(sources.map((source) => source.kind));
  const addable = available.filter((kind) => !connected.has(kind.kind));
  const chosen = addable.find((kind) => kind.kind === selected) ?? addable[0] ?? null;

  const test = async (source: KnowledgeSource) => {
    if (running === source.id) return;
    setRunning(source.id);
    setProblem(null);
    setNotice(null);
    try {
      const result = await api.testKnowledgeSource(workspaceId, source.id);
      setNotice(
        result.ok
          ? { tone: 'status', text: `${source.name}: 接続を確認しました（${result.detail}）` }
          : { tone: 'alert', text: `${source.name}: 接続できませんでした（${result.detail}）` },
      );
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setRunning(null);
    }
  };

  const setEnabled = async (source: KnowledgeSource, enabled: boolean) => {
    if (running === source.id) return;
    setRunning(source.id);
    setProblem(null);
    setNotice(null);
    try {
      await api.updateKnowledgeSource(
        workspaceId,
        source.id,
        { expectedRevision: source.revision, enabled },
        newIdempotencyKey(),
      );
      state.reload();
    } catch (error) {
      const next = toProblem(error);
      if (next.code === 'revision_conflict') {
        // 別の画面で変わっただけなので、赤いエラーにせず取り直す。
        setNotice({ tone: 'alert', text: 'ほかの変更で接続が更新されました。最新を読み込みました。' });
        state.reload();
      } else {
        setProblem(next);
      }
    } finally {
      setRunning(null);
    }
  };

  return (
    <div className="stack">
      {(state.loading || kinds.loading) && !state.data && <Spinner label="知識ソースを取得しています" />}
      <ProblemNotice
        problem={state.problem ?? kinds.problem ?? problem}
        action={
          // kinds の失敗もここに出すので、一覧だけ取り直すと kinds.problem が消えず画面が復帰できない。両方取り直す。
          <Button
            onClick={() => {
              state.reload();
              kinds.reload();
            }}
          >
            再取得
          </Button>
        }
      />
      {state.data && (
        <Table
          label="知識ソース一覧"
          columns={[
            { key: 'name', header: '名前' },
            { key: 'kind', header: '種類' },
            { key: 'description', header: '説明' },
            { key: 'status', header: '状態' },
            { key: 'actions', header: '操作' },
          ]}
          rows={sources.map((source) => {
            const descriptor = descriptors.get(source.kind) ?? null;
            // 記述子が無い種類は 422 unsupported_kind になるだけなので、編集と同じく押させない。
            const unusable = kindsKnown && descriptor === null;
            const busy = running === source.id;
            return {
              key: source.id,
              cells: {
                name: (
                  <span className="status-cell">
                    {source.name}
                    {source.builtin && <Badge tone="neutral">組み込み</Badge>}
                  </span>
                ),
                kind: <code>{source.kind}</code>,
                description: source.description || '—',
                status: (
                  <span className="status-cell">
                    <Badge tone={source.enabled ? 'completed' : 'neutral'}>{source.enabled ? '有効' : '無効'}</Badge>
                    {!source.builtin && kindsKnown && descriptor === null && (
                      <Badge tone="neutral">使えない種類</Badge>
                    )}
                    {descriptor !== null && descriptor.credentialFields.length > 0 && (
                      <Badge tone={source.hasCredential ? 'completed' : 'awaiting'}>
                        {source.hasCredential ? '資格情報あり' : '資格情報なし'}
                      </Badge>
                    )}
                  </span>
                ),
                actions: source.builtin ? (
                  '—'
                ) : (
                  <span className="row knowledge-source-actions">
                    {/* 読み上げでは行が分からないので、名前にソース名を含める（E2E の手順もこの名前で押す）。 */}
                    <Button
                      variant="text"
                      aria-label={`${source.name} の接続を確認`}
                      disabled={unusable || busy}
                      onClick={() => void test(source)}
                    >
                      接続を確認
                    </Button>
                    <Button
                      variant="text"
                      aria-label={`${source.name} を編集`}
                      disabled={unusable}
                      onClick={() => descriptor !== null && setEditing({ kind: descriptor, source })}
                    >
                      編集
                    </Button>
                    <Button
                      variant="text"
                      aria-label={`${source.name} を${source.enabled ? '無効' : '有効'}にする`}
                      disabled={busy}
                      onClick={() => void setEnabled(source, !source.enabled)}
                    >
                      {source.enabled ? '無効にする' : '有効にする'}
                    </Button>
                    <Button
                      variant="text"
                      aria-label={`${source.name} を削除`}
                      disabled={busy}
                      onClick={() => setDeleting(source)}
                    >
                      削除
                    </Button>
                  </span>
                ),
              },
            };
          })}
        />
      )}
      {notice !== null && (
        <p className="hint" role={notice.tone === 'status' ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      <div className="row">
        <select
          className="input"
          aria-label="追加する種類"
          value={chosen?.kind ?? ''}
          disabled={addable.length === 0}
          onChange={(event) => setSelected(event.target.value)}
        >
          {addable.map((kind) => (
            <option key={kind.kind} value={kind.kind}>
              {kind.name}
            </option>
          ))}
        </select>
        <Button
          variant="primary"
          disabled={chosen === null}
          onClick={() => chosen !== null && setEditing({ kind: chosen, source: null })}
        >
          接続を追加
        </Button>
        {kinds.data && available.length === 0 && <span className="hint">追加できる種類はありません</span>}
      </div>
      {editing !== null && (
        <KnowledgeSourceDialog
          workspaceId={workspaceId}
          kind={editing.kind}
          source={editing.source}
          onSaved={() => {
            setEditing(null);
            state.reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {deleting !== null && (
        <DeleteDialog
          kind="接続"
          name={deleting.name}
          consequences={['この接続の設定と資格情報', 'Studio の AI がこのソースを使えなくなります']}
          onDelete={async (idempotencyKey) => {
            await api.deleteKnowledgeSource(workspaceId, deleting.id, idempotencyKey);
            setDeleting(null);
            state.reload();
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
